import { Controller } from "stimulus";
import SupercastUploadManager, { isBackgroundUploadEnabled, logUploadState as postLogUploadState } from "packs/episode_upload_manager";

const ALLOWED_TYPES = [
    "audio/mp3",
    "audio/mpeg",
    "audio/wav",
    "video/mp4",
    "video/quicktime"
];

const AUDIO_TYPES = [
    "audio/mp3",
    "audio/mpeg",
    "audio/wav",
];

const VIDEO_TYPES = [
    "video/mp4",
    "video/quicktime",
];

function extractDuration(file, onResolved) {
    const objectURL = URL.createObjectURL(file);
    const mediaEl = document.createElement("audio");
    mediaEl.src = objectURL;
    mediaEl.onloadedmetadata = () => {
        onResolved(mediaEl.duration);
        URL.revokeObjectURL(objectURL);
    };
}

export default class UploadController extends Controller {
    connect() {
        this.uploading = [];
        this._uploadStats = {};

        // Behind the `background_episode_upload` flag (exposed via the persistent
        // `data-background-episode-upload-enabled` attribute in creator_dashboard.html.erb),
        // uploads are driven by the SupercastUploadManager singleton + a dedicated Worker instead
        // of Uppy, so the transfer survives this controller disconnecting (modal close / Turbo
        // navigation). `this.files` mirrors the small subset of Uppy's per-file shape
        // ({id, name, type, data, meta}) that the shared preview/progress helpers below rely on -
        // it's only populated in this code path.
        this.files = [];
        this.useManager = isBackgroundUploadEnabled();

        if (this.useManager) {
            this._unsubscribeManager = SupercastUploadManager.subscribe(this.handleManagerEvent.bind(this));
            this.reattachInFlightUploads();
        } else {
            this.uppy = new window.Uppy.Uppy(this.uppyConfig());
            this.attachTus(this.uppy);
        }

        this.extracting = false;
        if (this.hasEpisodeIdValue && this.episodeIdValue) {
            this.checkExtractionStatusAndMaybePoll();
        }
    }

    disconnect() {
        if (this._unsubscribeManager) {
            this._unsubscribeManager();
            this._unsubscribeManager = null;
        }
    }

    dropZoneTargetConnected(el) {
        const prevent = (e) => {
            e.preventDefault();
            e.stopPropagation();
        };

        let icon = document.getElementById("cloud");

        el.addEventListener("dragover", prevent);
        el.addEventListener("dragenter", (e) => {
            prevent(e);
            icon.classList.add("text-blue-700");
            icon.classList.remove("text-gray-500");
        });
        el.addEventListener("dragleave", (e) => {
            prevent(e);
            icon.classList.add("text-gray-500");
            icon.classList.remove("text-blue-700");
        });

        el.addEventListener("drop", (e) => {
            e.preventDefault();

            const file = e.dataTransfer.files[0];
            this.addOrReplaceFile(file);
        });
    }

    eventTargetConnected(el) {
        el.addEventListener("upload-started", (e) => {
            const { episodeId } = e.detail;
            if (episodeId === null) return;

            window.Turbo.visit(`/dashboard/channels/episodes/${episodeId}/detail`, { frame: "step" });
        });
    }

    previewVideoTargetConnected(el) {
        if (this.hasVideoImageInputTarget) {
            this.videoImageInputTarget.addEventListener("change", () => {
                if (FileReader && this.videoImageInputTarget.files && this.videoImageInputTarget.files.length) {
                    var fr = new FileReader();
                    fr.onload = function () {
                        el.poster = fr.result;
                        el.load(); // Reset the video so the new thumbnail displays
                    }
                    fr.readAsDataURL(this.videoImageInputTarget.files[0]);
                }
            });
        }

        const video = this.getFileByType(VIDEO_TYPES);
        if (!video || !this.hasPreviewVideoTarget) return;

        this.updateVideoPreview(video);
    }

    previewAudioTargetConnected(el) {
        if (this.hasAudioImageInputTarget) {
            this.audioImageInputTarget.addEventListener("change", () => {
                if (FileReader && this.audioImageInputTarget.files && this.audioImageInputTarget.files.length) {
                    var fr = new FileReader();
                    fr.onload = function () {
                        document.getElementById("preview-image").src = fr.result;
                    }
                    fr.readAsDataURL(this.audioImageInputTarget.files[0]);
                }
            });
        }

        const audio = this.getFileByType(["audio/mp3", "audio/mpeg", "audio/wav"]);
        if (!audio || !this.hasPreviewAudioTarget) return;

        this.updateAudioPreview(audio);
    }

    extractionNoticeTargetConnected(el) {
        this.toggleExtractionNotice();
    }

    videoOnlyTargetConnected(el) {
        const video = this.getFileByType(VIDEO_TYPES);
        if (!video) return;

        el.classList.remove("hidden");
    }

    audioOnlyTargetConnected(el) {
        const audio = this.getFileByType(AUDIO_TYPES);
        if (!audio) return;

        el.classList.remove("hidden");
    }

    premiereFieldTargetConnected(el) {
        if (this.isVideoUpload()) this.enablePremiereField();
    }

    isVideoUpload() {
        // Reuse the signal that backs the videoOnly/audioOnly targets - no second derivation.
        return !!this.getFileByType(VIDEO_TYPES);
    }

    // One-directional: only ever enables. The server render (has_video?) is authoritative on the way back.
    enablePremiereField() {
        this.premiereFieldTargets.forEach((el) => {
            el.classList.remove("opacity-50", "cursor-not-allowed");
            const checkbox = el.querySelector("input[type=checkbox]");
            if (checkbox) checkbox.disabled = false;
        });
        this.premiereHintTargets.forEach((el) => {
            if (el.dataset.readyText) el.textContent = el.dataset.readyText;
        });
    }

    toggleExtractionNotice() {
        if (this.hasExtractionNoticeTarget) {
            if (this.extracting) {
                this.extractionNoticeTarget.classList.remove('hidden');
                this.previewAudioTarget.parentElement.classList.add('hidden');
            } else {
                this.extractionNoticeTarget.classList.add('hidden');
            }
        }
    }

    updateAudioPreview(audio) {
        if (this.hasPreviewAudioTarget) {
            const audioEl = this.previewAudioTarget.querySelector("audio");
            audioEl.src = URL.createObjectURL(audio.data);
            this.previewAudioTarget.parentElement.classList.remove("hidden");
        }
    }

    updateVideoPreview(video) {
        if (this.hasPreviewVideoTarget) {
            const current = this.previewVideoTarget.querySelector("source");
            const source = document.createElement("source");
            source.src = URL.createObjectURL(video.data);
            source.type = video.type;
            if (current) {
                this.previewVideoTarget.removeChild(current);
            }
            this.previewVideoTarget.appendChild(source);
            this.previewVideoTarget.load(); // Force a reload after changing
            this.previewVideoTarget.parentElement.classList.remove("hidden");
        }
    }

    getFileByType(fileTypes) {
        const files = this.useManager ? this.files : this.uppy.getFiles();
        for (const file of files) {
            if (fileTypes.includes(file.type)) return file;
        }
        return null;
    }

    // Thin wrapper so call sites below don't need to know this.episodeIdValue - the actual
    // POST /log_upload logic is shared with SupercastUploadManager's own upload-state logging.
    logUploadState(upload_log_id, state, error_message = null) {
        postLogUploadState(this.episodeIdValue, upload_log_id, state, error_message);
    }

    uppyConfig() {
        return {
            autoProceed: true,
            onBeforeFileAdded: this.onBeforeFileAdded.bind(this),
        };
    }

    addFile(e) {
        const file = e.target.files[0];
        this.addOrReplaceFile(file);
    }

    // Shared entry point for both the drag/drop zone and the file-input `addFile` action -
    // branches into the legacy Uppy flow or the manager-driven flow depending on the flag.
    addOrReplaceFile(file) {
        if (this.useManager) {
            this.addFileManaged(file);
            return;
        }

        let replace;
        if (VIDEO_TYPES.includes(file.type)) {
            replace = this.getFileByType(VIDEO_TYPES);
        } else {
            replace = this.getFileByType(AUDIO_TYPES);
        }
        if (replace) {
            this.uppy.removeFile(replace.id); // aborts the in-flight tus transfer for the displaced file, if any
            this.purgeReplacedMedia(replace);
        }
        this.uppy.addFile(file);
    }

    exit(e) {
        // The legacy Uppy path uploads on the main thread for as long as this controller/modal is
        // mounted, so closing mid-upload really does risk losing it - keep warning there. With
        // the manager-driven flow the transfer survives the modal closing entirely (that's the
        // whole point of this change), so there's nothing to warn about.
        if (!this.useManager && this.uploading.length > 0) {
            if (!window.confirm("Exiting now will cancel in progress uploads.\nAre you sure you want to close?")) {
                e.stopImmediatePropagation();
                return;
            }
        }

        window.Turbo.visit(`/dashboard/channels/episodes/${this.episodeIdValue}`);
    }

    async removeAudio(e) {
        await this.removeMedia({
            file: this.getFileByType(["audio/mp3", "audio/mpeg", "audio/wav"]),
            endpoint: "remove_audio",
            previewTarget: this.previewAudioTarget,
            onlyTargets: this.audioOnlyTargets,
        });
    }

    async removeVideo(e) {
        await this.removeMedia({
            file: this.getFileByType(VIDEO_TYPES),
            endpoint: "remove_video",
            previewTarget: this.previewVideoTarget,
            onlyTargets: this.videoOnlyTargets,
        });
    }

    // Shared by removeAudio/removeVideo - both drop the local file reference (cancelling any
    // in-flight upload for it), then tell the server to clear the committed media once nothing
    // else is still uploading. Differ only in which media type/endpoint/targets they touch.
    async removeMedia({ file, endpoint, previewTarget, onlyTargets }) {
        let uploadLogId, episodeId;

        if (file) {
            ({ uploadLogId, episodeId } = this.uploadLogInfoFor(file));

            if (this.useManager) {
                if (file.meta.localId) SupercastUploadManager.cancel(file.meta.localId);
                this.files.splice(this.files.indexOf(file), 1);
                this.uploading = this.uploading.filter((id) => id !== file.meta.localId);
            } else {
                this.uppy.removeFile(file.id);
                this.uploading = this.uploading.filter((id) => id !== uploadLogId);
            }
        }

        if (this.uploading.length == 0) {
            await this.purgeMedia(endpoint, { uploadLogId, episodeId });
        }

        previewTarget.parentElement.classList.add("hidden");
        onlyTargets.forEach(t => t.classList.add("hidden"));
    }

    // Read-only lookup of the upload_log_id (and, under the manager path, the resolved episodeId)
    // tracked for a locally-known file - so callers can tell the server which UploadLog to cancel
    // without duplicating the manager-vs-legacy branching everywhere. Doesn't touch any local
    // tracking state itself.
    uploadLogInfoFor(file) {
        if (!file) return {};

        if (this.useManager && file.meta.localId) {
            const tracked = SupercastUploadManager.getUpload(file.meta.localId);
            return {
                uploadLogId: file.meta.upload_log_id || (tracked && tracked.uploadLogId),
                episodeId: tracked && tracked.episodeId,
            };
        }

        return { uploadLogId: file.meta && file.meta.upload_log_id };
    }

    // The actual DELETE request - the same request path used by the explicit remove button
    // (removeMedia) and the replace flow (purgeReplacedMedia), so both hit the exact same
    // controller action/behavior (Dashboard::Channels::EpisodesController#remove_audio/
    // #remove_video), which also cancels the passed-in upload_log_id server-side if it's still open.
    async purgeMedia(endpoint, { uploadLogId, episodeId } = {}) {
        const targetEpisodeId = episodeId || (this.hasEpisodeIdValue ? this.episodeIdValue : null);
        if (!targetEpisodeId) return;

        const csrfToken = document.getElementsByName("csrf-token")[0].content;

        let formData = new FormData();
        formData.append("authenticity_token", csrfToken);
        if (uploadLogId) formData.append("upload_log_id", uploadLogId);

        const payload = {
            method: "DELETE",
            headers: {
                "Accept": "application/json",
            },
            body: formData
        };

        await fetch(`/dashboard/channels/episodes/${targetEpisodeId}/${endpoint}`, payload);
    }

    // Cancels the upload a new file selection is about to displace - aborts any in-flight
    // transfer (worker postMessage under the manager path; the tus request is already aborted by
    // uppy.removeFile under the legacy path, see addOrReplaceFile) and tells the server to purge
    // the attachment + cancel its UploadLog (if still open), so a reupload can never leave an
    // orphaned upload_log/attachment behind. Shared by addOrReplaceFile (legacy path) and
    // addFileManaged (manager path) - covers both replacing a file mid-upload and replacing an
    // already-fully-attached one (in the latter case there's no open upload_log to cancel, so this
    // degrades to the same attachment purge remove_audio/remove_video already do).
    purgeReplacedMedia(replaced) {
        const { uploadLogId, episodeId } = this.uploadLogInfoFor(replaced);

        if (this.useManager && replaced.meta.localId) {
            SupercastUploadManager.cancel(replaced.meta.localId);
        }
        this.uploading = this.uploading.filter((id) => id !== (replaced.meta.localId || uploadLogId));

        const endpoint = VIDEO_TYPES.includes(replaced.type) ? "remove_video" : "remove_audio";
        this.purgeMedia(endpoint, { uploadLogId, episodeId });
    }

    onBeforeFileAdded(file, files) {
        if (!ALLOWED_TYPES.includes(file.type)) {
            this.errorTarget.innerText = "Unsupported file type provided: " + file.type;
            return false
        }

        // Pull media duration for metadata.
        extractDuration(file.data, (duration) => { file.meta.duration = duration; });

        return true;
    }

    async fileAdded(file) {
        if (this.hasWorkingTarget) {
            this.workingTarget.classList.remove("hidden");
        }

        // A video was selected - live-enable the premiere field, independent of upload progress.
        if (VIDEO_TYPES.includes(file.type)) {
            this.enablePremiereField();
        }

        const mediaType = file.type.split("/")[0];

        const url = new URL(window.location.origin + "/dashboard/channels/episodes/presigned_upload");

        url.searchParams.append("type", mediaType);
        url.searchParams.append("title", file.name);
        url.searchParams.append("episode", this.episodeIdValue);
        if (this.hasDynamicValue && this.hasTriggerEventValue) {
            url.searchParams.append("dynamic", this.dynamicValue);
            url.searchParams.append("trigger_event", this.triggerEventValue);
        }

        await fetch(url.toString())
            .then(response => response.json())
            .then(data => {
                const { episode_id, upload_log_id, ...bunny } = data;
                this.episodeIdValue = episode_id;
                file.meta.bunny = bunny;
                file.meta.upload_log_id = upload_log_id;

                this.uploading.push(upload_log_id);

                if (this.hasEventTarget) {
                    const event = new CustomEvent("upload-started", { detail: { episodeId: this.episodeIdValue } });
                    this.eventTarget.dispatchEvent(event);
                }

                if (VIDEO_TYPES.includes(file.type)) {
                    this.updateVideoPreview(file)
                } else {
                    this.updateAudioPreview(file);
                }

                this.logUploadState(file.meta.upload_log_id, "upload_started");
            })
            .finally(() => {
                if (this.hasWorkingTarget) {
                    this.workingTarget.classList.remove("hidden");
                }
            });
    }

    // ---------------------------------------------------------------------------------------
    // Manager-driven flow (flag on). Uppy is entirely bypassed: files are tracked in `this.files`
    // (a small array of {id, name, type, data, meta} wrappers, mirroring the subset of Uppy's
    // shape the shared preview/progress helpers above rely on) and the actual transfer is handed
    // off to SupercastUploadManager, which owns the Worker + all Rails HTTP calls and survives
    // this controller disconnecting.
    // ---------------------------------------------------------------------------------------

    addFileManaged(file) {
        if (!ALLOWED_TYPES.includes(file.type)) {
            this.errorTarget.innerText = "Unsupported file type provided: " + file.type;
            return;
        }

        // No `id` field here (unlike Uppy's file objects, which use it for
        // removeFile/retryUpload) - the managed path identifies files by `meta.localId` instead,
        // assigned below once SupercastUploadManager.start() returns it.
        const fileWrapper = { name: file.name, type: file.type, data: file, meta: {} };

        // Pull media duration for metadata, same as onBeforeFileAdded does for the legacy path.
        // Parsing is normally near-instant, but isn't guaranteed to resolve before
        // SupercastUploadManager.start() reads `fileWrapper.meta.duration` below, so we also patch
        // it in after the fact via updateUpload() once/if it resolves later.
        extractDuration(file, (duration) => {
            fileWrapper.meta.duration = duration;
            if (fileWrapper.meta.localId) {
                SupercastUploadManager.updateUpload(fileWrapper.meta.localId, { duration });
            }
        });

        // Look up (and purge) any existing file of the same media type *before* registering the
        // new one - registerFileWrapper is also used by reattachUpload to idempotently re-render
        // an already-tracked upload after a Turbo navigation, so the purge can't live there
        // without risking a false "replace" the first time an in-flight upload is reattached.
        const replace = VIDEO_TYPES.includes(fileWrapper.type)
            ? this.getFileByType(VIDEO_TYPES)
            : this.getFileByType(AUDIO_TYPES);
        if (replace) this.purgeReplacedMedia(replace);

        this.registerFileWrapper(fileWrapper);

        this.fileAddedManaged(fileWrapper);
    }

    // Splices out any existing entry of the same media type, tracks the new one, and refreshes
    // its preview - shared by addFileManaged (brand-new file) and reattachUpload (rehydrating a
    // file the manager already knows about after a Turbo navigation).
    registerFileWrapper(fileWrapper) {
        const replace = VIDEO_TYPES.includes(fileWrapper.type)
            ? this.getFileByType(VIDEO_TYPES)
            : this.getFileByType(AUDIO_TYPES);
        if (replace) this.files.splice(this.files.indexOf(replace), 1);
        this.files.push(fileWrapper);

        if (VIDEO_TYPES.includes(fileWrapper.type)) {
            this.updateVideoPreview(fileWrapper);
        } else {
            this.updateAudioPreview(fileWrapper);
        }
    }

    fileAddedManaged(fileWrapper) {
        if (this.hasWorkingTarget) {
            this.workingTarget.classList.remove("hidden");
        }

        const mediaType = fileWrapper.type.split("/")[0];

        const localId = SupercastUploadManager.start({
            file: fileWrapper.data,
            episodeId: this.hasEpisodeIdValue ? this.episodeIdValue : null,
            mediaType,
            dynamic: this.hasDynamicValue ? this.dynamicValue : undefined,
            triggerEvent: this.hasTriggerEventValue ? this.triggerEventValue : undefined,
            duration: fileWrapper.meta.duration,
        });
        fileWrapper.meta.localId = localId;

        this.uploading.push(localId);

        // Core UX change: the Publish button is no longer blocked on the raw upload finishing -
        // the episode can be saved/published while it's still transferring in the background
        // (backend guards this via Episode#upload_in_progress?/#publish_allowed?). Un-hide it the
        // moment the upload *starts*, not only once it completes.
        this.showUploadedTargets.forEach(t => t.hidden = false);
    }

    // Re-renders any upload already tracked by the manager for this episode - what makes
    // disconnect/reconnect across a Turbo navigation safe, since upload state lives in the
    // manager singleton, not this controller instance.
    reattachInFlightUploads() {
        if (!this.hasEpisodeIdValue || !this.episodeIdValue) return;

        SupercastUploadManager.getUploadsForEpisode(this.episodeIdValue).forEach((upload) => this.reattachUpload(upload));
    }

    reattachUpload(upload) {
        if (!upload.file) return; // Cached File didn't survive (e.g. a hard reload) - nothing to preview.

        const fileWrapper = {
            name: upload.file.name,
            type: upload.file.type,
            data: upload.file,
            meta: { localId: upload.localId, upload_log_id: upload.uploadLogId, duration: upload.duration },
        };

        this.registerFileWrapper(fileWrapper);

        if (upload.state === "uploading" || upload.state === "processing") {
            this.uploading.push(upload.localId);
            this.showUploadedTargets.forEach(t => t.hidden = false);
            this.individualProgressManaged(upload);
        } else if (upload.state === "completed") {
            this.onManagedUploadCommitted(upload);
        } else if (upload.state === "error") {
            this.onManagedUploadFailed(upload);
        }
    }

    handleManagerEvent(event) {
        const { type, upload } = event;
        if (!upload) return;

        const belongsToThisEpisode =
            (this.hasEpisodeIdValue && this.episodeIdValue && String(upload.episodeId) === String(this.episodeIdValue)) ||
            this.uploading.includes(upload.localId);
        if (!belongsToThisEpisode) return;

        switch (type) {
            case "episode-resolved": {
                // `hasEpisodeIdValue` is false for a brand-new episode (no
                // data-...-episode-id-value attribute yet), which is exactly the case this event
                // exists to resolve - do NOT gate on it, or the very first upload for a new
                // episode never navigates to the next step. Matches the legacy Uppy path's
                // fileAdded(), which always assigns + dispatches unconditionally.
                const changed = this.episodeIdValue !== upload.episodeId;
                this.episodeIdValue = upload.episodeId;

                if (changed && this.hasEventTarget) {
                    const navEvent = new CustomEvent("upload-started", { detail: { episodeId: upload.episodeId } });
                    this.eventTarget.dispatchEvent(navEvent);
                }
                break;
            }
            case "progress":
                this.individualProgressManaged(upload);
                break;
            case "commit-success":
                this.onManagedUploadCommitted(upload);
                break;
            case "commit-error":
            case "upload-error":
                this.onManagedUploadFailed(upload);
                break;
            default:
                break;
        }
    }

    individualProgressManaged(upload) {
        const fileLike = {
            id: upload.localId,
            type: upload.file.type,
            meta: { upload_log_id: upload.uploadLogId },
        };
        this.individualProgress(fileLike, { bytesUploaded: upload.bytesUploaded, bytesTotal: upload.bytesTotal });
    }

    onManagedUploadCommitted(upload) {
        const isVideo = upload.mediaType === "video";

        // Scoped to the media type that actually finished
        if (isVideo) {
            this.showVideoErrorTargets.forEach(t => t.hidden = true);
            this.showVideoUploadingTargets.forEach(t => t.hidden = true);
            this.showVideoUploadedTargets.forEach(t => t.hidden = false);
        } else {
            this.showAudioErrorTargets.forEach(t => t.hidden = true);
            this.showAudioUploadingTargets.forEach(t => t.hidden = true);
            this.showAudioUploadedTargets.forEach(t => t.hidden = false);
        }

        this.uploading = this.uploading.filter(id => id !== upload.localId);
        if (this.uploading.length == 0) {
            this.showUploadingTargets.forEach(t => t.hidden = true);
            this.showUploadedTargets.forEach(t => t.hidden = false);
        }

        // If a video was uploaded and audio extraction feature is enabled, start polling status
        if (isVideo) {
            this.extracting = true;
            this.toggleExtractionNotice();
            this.startExtractionPolling();
        }
    }

    onManagedUploadFailed(upload) {
        if (upload.mediaType === "video") {
            this.showVideoErrorTargets.forEach(t => t.hidden = false);
        } else {
            this.showAudioErrorTargets.forEach(t => t.hidden = false);
        }

        // Unlike the legacy Uppy path, there's no automatic retry loop here - the transfer (or
        // commit) already failed after the worker/manager's own retry attempts, and re-driving a
        // whole new transfer automatically would be wasteful. Retry is user-driven, via the
        // upload status widget's Retry button (Block::UploadStatusWidgetComponent), which calls
        // `SupercastUploadManager.retry(localId)`.
    }

    // This is for the publish button progress, not individual file progress
    progress(percentage) {
        if (this.hasShowUploadingTarget && percentage < 100) {
            this.showUploadingTargets.forEach(t => t.hidden = false);
        }
        if (this.hasShowUploadedTarget && percentage < 100) {
            this.showUploadedTargets.forEach(t => t.hidden = true);
        }
        if (this.hasUploadPercentageTarget) {
            this.uploadPercentageTargets.forEach(t => t.innerText = `Uploading... ${percentage}%`);
        }
    }

    individualProgress(file, progress) {
        const percentage = Math.round(progress.bytesUploaded / progress.bytesTotal * 100);

        if (VIDEO_TYPES.includes(file.type)) {
            if (percentage < 100) {
                if (this.hasShowVideoUploadingTarget) {
                    this.showVideoUploadingTargets.forEach(t => t.hidden = false);
                }
                if (this.hasShowVideoUploadedTarget) {
                    this.showVideoUploadedTargets.forEach(t => t.hidden = true);
                }
            }
            if (this.hasVideoUploadPercentageTarget) {
                this.videoUploadPercentageTargets.forEach(t => t.innerText = `Uploading... ${percentage}%`);
            }
            // compute and render speed/ETA
            const now = Date.now();
            const stats = this._getOrInitStats(file.id, now);
            const dt = (now - stats.lastTime) / 1000;
            const minSampleS = 0.5;
            if (dt >= minSampleS) {
                const db = progress.bytesUploaded - stats.lastBytes;
                const inst = db / dt;
                const ema = this._emaBps(stats.speedBps, inst); // moving average
                stats.speedBps = ema;
                stats.lastTime = now;
                stats.lastBytes = progress.bytesUploaded;
            }
            const elapsedS = (now - stats.startedAt) / 1000;
            const avgBps = this._avgBps(progress.bytesUploaded, elapsedS);
            const hybridBps = this._hybridBps(stats.speedBps, avgBps);
            // hide stats if less than 3 seconds or 5MB uploaded
            const warmedUp = this._shouldShowStats(elapsedS, progress.bytesUploaded);
            let statsHtml = "";
            if (warmedUp && hybridBps > 0) {
                const remainingBytes = Math.max(progress.bytesTotal - progress.bytesUploaded, 0);
                const secondsRemaining = remainingBytes / hybridBps;
                const speedText = `${this._formatBytes(hybridBps)}/s`;
                const etaText = `about ${this._formatDuration(secondsRemaining)} left`;
                statsHtml = `${speedText}, ${etaText}`;
            }
            this._updateVideoUploadStats(statsHtml);
        } else {
            if (percentage < 100) {
                if (this.hasShowAudioUploadingTarget) {
                    this.showAudioUploadingTargets.forEach(t => t.hidden = false);
                }
                if (this.hasShowAudioUploadedTarget) {
                    this.showAudioUploadedTargets.forEach(t => t.hidden = true);
                }
            }
            if (this.hasAudioUploadPercentageTarget) {
                this.audioUploadPercentageTargets.forEach(t => t.innerText = `Uploading... ${percentage}%`);
            }
        }

        if (percentage == 100) {
            // Under the manager-driven flow, log_upload("processing_started") is already fired by
            // SupercastUploadManager itself as soon as the worker reports upload-complete.
            if (!this.useManager) {
                this.logUploadState(file.meta.upload_log_id, "processing_started");
            }
            delete this._uploadStats[file.id];
            // hide stats once upload completes
            if (VIDEO_TYPES.includes(file.type)) {
                if (this.hasVideoUploadStatsTarget) {
                    this.videoUploadStatsTargets.forEach(t => { t.innerText = ""; t.hidden = true; });
                }
            }
        }
    }

    async commitUpload(file, _) {
        let csrfToken = document.getElementsByName("csrf-token")[0].content;

        let formData = new FormData();
        formData.append("authenticity_token", csrfToken);

        const metadata = {
            size: file.size,
            filename: file.name,
            mime_type: file.type,
            duration: file.meta.duration,
        };
        if (VIDEO_TYPES.includes(file.type)) {
            formData.append("episode[podcast_video]", file.meta.bunny.video_id);
            formData.append("episode[podcast_video_meta]", JSON.stringify(metadata));
        } else {
            formData.append("episode[podcast_audio]", file.meta.bunny.video_id);
            formData.append("episode[podcast_audio_meta]", JSON.stringify(metadata));
        }
        formData.append("upload_log_id", file.meta.upload_log_id);

        const payload = {
            method: "PATCH",
            headers: {
                "Accept": "application/json",
            },
            body: formData,
        };

        let attempts = 0;
        let response;
        while (attempts < 3) {
            response = await fetch(`/dashboard/channels/episodes/${this.episodeIdValue}`, payload);
            if (response.ok) {
                if (AUDIO_TYPES.includes(file.type)) {
                    this.showAudioErrorTargets.forEach(t => t.hidden = true);
                } else {
                    this.showVideoErrorTargets.forEach(t => t.hidden = true);
                }
                break; // Exit the loop if the request was successful
            }
            attempts++;
        }

        if (!response.ok) {
            this.logUploadState(file.meta.upload_log_id, "error", `Commit failed with status ${response.status}`);
            this.retryUpload(file, null, null);
            return;
        }

        // Show uploaded state
        this.showVideoUploadingTargets.forEach(t => t.hidden = true);
        this.showVideoUploadedTargets.forEach(t => t.hidden = false);
        this.showAudioUploadingTargets.forEach(t => t.hidden = true);
        this.showAudioUploadedTargets.forEach(t => t.hidden = false);

        this.uploading.splice(this.uploading.indexOf(file.meta.upload_log_id), 1);
        if (this.uploading.length == 0) {
            this.showUploadingTargets.forEach(t => t.hidden = true);
            this.showUploadedTargets.forEach(t => t.hidden = false);
        }

        // If a video was uploaded and audio extraction feature is enabled, start polling status
        if (VIDEO_TYPES.includes(file.type)) {
            // Mark extracting now; controller endpoint will have queued the job on update
            this.extracting = true;
            this.toggleExtractionNotice();
            this.startExtractionPolling();
        }
    }

    retryUpload(file, error, response) {
        if (AUDIO_TYPES.includes(file.type)) {
            this.showAudioErrorTargets.forEach(t => t.hidden = false);
        } else {
            this.showVideoErrorTargets.forEach(t => t.hidden = false);
        }

        if (error) {
            this.logUploadState(file.meta.upload_log_id, "error", error.message);
        }

        // Automatically retry uploads on failure. Upload failures are usually intermittent.
        this.uppy.retryUpload(file.id);
    }

    attachTus(uppy) {
        uppy.use(window.Uppy.Tus, {
            endpoint: "https://video.bunnycdn.com/tusupload",
            async onBeforeRequest(req, file) {
                var data = file.meta.bunny

                req.setHeader('AuthorizationSignature', data.signature);
                req.setHeader('AuthorizationExpire', data.expire);
                req.setHeader('VideoId', data.video_id);
                req.setHeader('LibraryId', data.library_id);
                // Custom correlation headers for provider diagnostics
                try {
                    if (file?.meta?.upload_log_id) req.setHeader('X-Upload-Trace-Id', file.meta.upload_log_id);
                } catch (_) {}
            },
        });

        uppy.on('file-added', this.fileAdded.bind(this))
            .on('progress', this.progress.bind(this))
            .on('upload-progress', this.individualProgress.bind(this))
            .on('upload-success', this.commitUpload.bind(this))
            .on('upload-error', this.retryUpload.bind(this))
    }

    // Extraction helpers

    onExtractionComplete() {
        if (this.hasAddAudioTarget) {
            this.addAudioTarget.classList.add("hidden");
        }
        if (this.hasPreviewAudioTarget) {
            this.previewAudioTarget.parentElement.classList.remove("hidden");
        }
    }

    async checkExtractionStatusAndMaybePoll() {

        try {
            const res = await fetch(`/dashboard/channels/episodes/${this.episodeIdValue}/audio_extraction_status`);
            if (!res.ok) return;
            const data = await res.json();
            const status = data.status;
            if (["queued", "processing"].includes(status)) {
                this.extracting = true;
                this.toggleExtractionNotice();
                this.startExtractionPolling();
            } else {
                this.extracting = false;
                this.toggleExtractionNotice();
                if (status === "done") {
                    this.onExtractionComplete();
                }
            }
        } catch (e) { /* noop */ }
    }

    startExtractionPolling() {
        if (this._extractionPollTimer) return; // already polling
        const poll = async () => {
            try {
                const res = await fetch(`/dashboard/channels/episodes/${this.episodeIdValue}/audio_extraction_status`);
                if (!res.ok) return;
                const { status } = await res.json();
                if (["queued", "processing"].includes(status)) {
                    this.extracting = true;
                    this.toggleExtractionNotice();
                } else {
                    this.extracting = false;
                    this.toggleExtractionNotice();
                    if (this.hasAudioExtractionHintTarget) {
                        this.audioExtractionHintTarget.classList.add("hidden");
                    }
                    clearInterval(this._extractionPollTimer);
                    this._extractionPollTimer = null;
                    if (status === "done") {
                        this.onExtractionComplete();
                    }
                    try {
                        if (window.Turbo && this.hasEventTarget) {
                            window.Turbo.visit(`/dashboard/channels/episodes/${this.episodeIdValue}/detail`, { frame: "step" });
                        }
                    } catch (_) {}
                }
            } catch (e) { /* noop */ }
        };
        poll();
        this._extractionPollTimer = setInterval(poll, 4000);
    }

    async retryExtraction() {
        const hadRetryTarget = this.hasRetryControlTarget;
        if (hadRetryTarget) {
            this.retryControlTarget.classList.add("hidden");
        }

        try {
            const csrfToken = document.getElementsByName("csrf-token")[0].content;
            let formData = new FormData();
            formData.append("authenticity_token", csrfToken);

            const res = await fetch(`/dashboard/channels/episodes/${this.episodeIdValue}/retry_audio_extraction`, {
                method: "POST",
                headers: { "Accept": "application/json" },
                body: formData
            });
            if (!res.ok) {
                if (hadRetryTarget) this.retryControlTarget.classList.remove("hidden");
                return;
            }

            this.extracting = true;
            this.toggleExtractionNotice();
            this.startExtractionPolling();
        } catch (_) {
            if (hadRetryTarget) this.retryControlTarget.classList.remove("hidden");
        }
    }

    async cancelExtractionOrRemoveAudio() {
        // If currently extracting, cancel; otherwise, remove audio if present
        if (this.extracting) {
            try {
                const csrfToken = document.getElementsByName("csrf-token")[0].content;
                let formData = new FormData();
                formData.append("authenticity_token", csrfToken);
                await fetch(`/dashboard/channels/episodes/${this.episodeIdValue}/cancel_audio_extraction`, { method: "POST", body: formData, headers: { "Accept": "application/json" } });
            } finally {
                this.extracting = false;
                this.toggleExtractionNotice();
                // Immediately hide the extraction hint on cancel click
                if (this.hasAudioExtractionHintTarget) {
                    this.audioExtractionHintTarget.classList.add("hidden");
                }
                if (this._extractionPollTimer) { clearInterval(this._extractionPollTimer); this._extractionPollTimer = null; }
            }
        } else {
            // Fall back to removing audio via existing action
            await this.removeAudio();
        }
    }

    _getOrInitStats(fileId, now) {
        if (!this._uploadStats[fileId]) {
            this._uploadStats[fileId] = { startedAt: now, lastTime: now, lastBytes: 0, speedBps: 0 };
        }
        return this._uploadStats[fileId];
    }

    _emaBps(prev, inst) {
        return prev ? (prev * 0.7 + inst * 0.3) : inst;
    }

    _avgBps(bytesUploaded, elapsedS) {
        return elapsedS > 0 ? (bytesUploaded / elapsedS) : 0;
    }

    _hybridBps(speedBps, avgBps) {
        return (speedBps && avgBps) ? (0.5 * speedBps + 0.5 * avgBps) : (speedBps || avgBps);
    }

    _shouldShowStats(elapsedS, bytesUploaded) {
        return elapsedS >= 3 && bytesUploaded >= 5 * 1024 * 1024;
    }

    _updateVideoUploadStats(statsHtml) {
        if (this.hasVideoUploadStatsTarget) {
            this.videoUploadStatsTargets.forEach(t => {
                t.innerHTML = statsHtml;
                if (statsHtml) {
                    t.classList.remove("hidden");
                } else {
                    t.classList.add("hidden");
                }
            });
        }
    }

    _formatBytes(bytes) {
        if (!isFinite(bytes) || bytes <= 0) return "0 B";
        const units = ["B", "KB", "MB", "GB", "TB"];
        const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
        const value = bytes / Math.pow(1024, i);
        return `${value >= 100 ? value.toFixed(0) : value >= 10 ? value.toFixed(1) : value.toFixed(2)} ${units[i]}`;
    }

    _formatDuration(totalSeconds) {
        const s = Math.max(0, Math.round(totalSeconds));
        const hours = Math.floor(s / 3600);
        const minutes = Math.floor((s % 3600) / 60);
        const seconds = s % 60;
        if (hours > 0) {
            return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
        }
        return `${minutes}:${String(seconds).padStart(2, "0")}`;
    }
}

UploadController.targets = [
    "event", "dropZone", "working", "error", "previewVideo", "previewAudio",
    "videoImageInput", "audioImageInput",
    "showUploading", "showUploaded", "uploadPercentage",
    "showAudioUploading", "showAudioUploaded", "audioUploadPercentage", "showAudioError",
    "showVideoUploading", "showVideoUploaded", "videoUploadPercentage", "showVideoError", "videoUploadStats",
    "extractionNotice", "videoOnly", "audioOnly", "retryControl", "addAudio", "audioExtractionHint",
    "premiereField", "premiereHint"
];
UploadController.values = { episodeId: Number, dynamic: Boolean, triggerEvent: String };
